import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { useSalonStore } from '../../store/SalonStore';
import { useNotification } from '../../components/Notification/NotificationProvider';
import { Typography, Box, Divider, Grid, Container, Select, MenuItem } from '@mui/material';
import { InfoBlock } from '../../components';
import { SalonInfoMap } from '../../types';
import { filterAndRenameObjectKeys } from '../../utils';
import { SalonDashboardWrapper, StyledInfoBlockWrapper } from './SalonDashboard.styles';
import { DateRangeControl } from '../../components/Dashboard/DateRangeControl/DateRangeControl';
import { useDateRange } from '../../components/Dashboard/DateRangeControl/useDateRange';
import { SingleKpiBlock } from '../../components/Dashboard/SingleKpiBlock/SingleKpiBlock';
import { MultipleKpisChart } from '../../components/Dashboard/Chart/MultipleKpisChart/MultipleKpisChart';
import { useDashboardData } from './useDashboardData';
import { SalonSelect } from '../../components/Dashboard/SalonSelect/SalonSelect';
import { useTranslation } from 'react-i18next';
import { KpisByPlanChart } from '../../components/Dashboard/Chart/KpisByPlanChart/KpisByPlanChart';
import { BASE_FIELD_BACKGROUND_COLOR } from '../../themes/tokens';
import { PlanPreviousDateRangeDatasetKey } from './types';
import { KPI_PLAN_TYPES, MD_SIZES, PLAN_PREVIOUS_DATE_RANGE_DATASET } from './constants';
import {
  getPercentKpisByPlanType,
  getMultipleKpisChartData,
  getActualRpKpis,
  mapCYToFYPeriod,
  getPlanPreviousDateRangeCopy,
} from './helpers';
import { useResponsive } from '../../hooks/useResponsive';
import { CustomLegend } from '../../components/Dashboard/Chart/CustomLegend/CustomLegend';

export const SalonDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { salon, fetchSalon, resetSalon } = useSalonStore();
  const { showMessage } = useNotification();
  const { salonId } = useParams();
  const { isMobile } = useResponsive();

  const { dateRange, setDateRange } = useDateRange();
  const { data, isLoading } = useDashboardData({ salonId: salonId, dateRange: dateRange });

  const multipleKpisChartData = getMultipleKpisChartData(data);

  const defaultPlanPreviousDateRange = Object.keys(
    PLAN_PREVIOUS_DATE_RANGE_DATASET,
  )[0] as PlanPreviousDateRangeDatasetKey;
  const [planPreviousDateRange, setPlanPreviousDateRange] =
    useState<PlanPreviousDateRangeDatasetKey>(defaultPlanPreviousDateRange);

  useEffect(() => {
    if (salonId) {
      fetchSalon(salonId, showMessage, navigate);
    } else {
      resetSalon();
    }
  }, [salonId]);

  useEffect(() => {
    setPlanPreviousDateRange(defaultPlanPreviousDateRange);
  }, [dateRange]);

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        width: {
          xs: '90vw',
          sm: '90vw',
          md: '90vw',
        },
        maxWidth: '1650px',
        minWidth: '300px',
        margin: '16px auto',
      }}
    >
      <Box display="flex" justifyContent="center" mt="16px" mb="16px">
        <Typography variant={isMobile ? 'h4' : 'h2'} textAlign="center">
          {t('dashboard.salonDashboardHeader')}
        </Typography>
      </Box>
      <SalonDashboardWrapper>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={3}>
            <SalonSelect />
          </Grid>
          <Grid item xs={12} sm={12} md={5}>
            <StyledInfoBlockWrapper>
              {salon && (
                <InfoBlock
                  infoList={filterAndRenameObjectKeys(salon, SalonInfoMap)}
                  additionalInfo={
                    dateRange.name === 'QUARTER'
                      ? mapCYToFYPeriod(`${dateRange.year}`, dateRange.quarter)
                      : undefined
                  }
                />
              )}
            </StyledInfoBlockWrapper>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <DateRangeControl dateRange={dateRange} setDateRange={setDateRange} />
          </Grid>
        </Grid>
        <Grid container columns={7} spacing={2}>
          <Grid item xs={7} sm={7} md={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={12}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.numberOfNewGuests')}
                  data={data.tnNewGuestsServiced?.presentDateRange?.value.raw}
                  showPlaceholder={!salonId}
                  isLoading={isLoading?.tnNewGuestsServiced}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={12}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.numberOfGuests')}
                  data={data.tnGuestsServiced?.presentDateRange?.value.raw}
                  showPlaceholder={!salonId}
                  isLoading={isLoading?.tnGuestsServiced}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={7} sm={7} md={3}>
            <MultipleKpisChart
              title={t('dashboard.salonDashboardTitle')}
              data={multipleKpisChartData}
            />
          </Grid>

          <Grid item sm={7} md={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.numberOfServiceProviders')}
                  data={data.tnSp?.presentDateRange?.value.raw}
                  showPlaceholder={!salonId}
                  isLoading={isLoading?.tnSp}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.totalNumberServiceTransactions')}
                  data={data.tnServiceTransactions?.presentDateRange?.value.raw}
                  showPlaceholder={!salonId}
                  isLoading={isLoading?.tnServiceTransactions}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.totalNumberOfTransactions')}
                  data={data.tnTransactionsNumber?.presentDateRange?.value.raw}
                  showPlaceholder={!salonId}
                  prevData={data.tnTransactionsNumber?.previousDateRange?.value?.raw || undefined}
                  isLoading={isLoading?.tnTransactionsNumber}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.rpct')}
                  data={data.rpct?.presentDateRange?.value.raw}
                  showPlaceholder={!salonId}
                  isLoading={isLoading?.rpct}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.numberRetailOnlyTransactions')}
                  data={data.tnRetailOnlyTr?.presentDateRange?.value.raw}
                  showPlaceholder={!salonId}
                  isLoading={isLoading?.tnRetailOnlyTr}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SingleKpiBlock
                  label={t('dashboard.kpi.totalSales')}
                  data={
                    data.totalSales?.presentDateRange?.value
                      ? {
                          ...data.totalSales.presentDateRange.value.raw,
                          value: new Intl.NumberFormat('en-US').format(
                            data.totalSales.presentDateRange.value.raw.value,
                          ),
                        }
                      : undefined
                  }
                  showPlaceholder={!salonId}
                  isLoading={isLoading?.totalSales}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        <Box display="flex" flexDirection="column" gap={2}>
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
          >
            <Select
              value={planPreviousDateRange}
              onChange={(e) =>
                setPlanPreviousDateRange(e.target.value as PlanPreviousDateRangeDatasetKey)
              }
              sx={{
                minWidth: '30ch',
                width: isMobile ? '100%' : 'fit-content',
                height: 'fit-content',
                backgroundColor: BASE_FIELD_BACKGROUND_COLOR,
                '& .MuiSelect-select:focus': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              {Object.entries(PLAN_PREVIOUS_DATE_RANGE_DATASET).map(([key, { label }]) => {
                if (key === 'previousQuarter' && dateRange.name !== 'QUARTER') return;

                return (
                  <MenuItem key={key} value={key}>
                    {label}{' '}
                    {(key === 'lastYear' || key === 'previousQuarter') &&
                      `(${getPlanPreviousDateRangeCopy(dateRange, key)})`}
                  </MenuItem>
                );
              })}
            </Select>
            <CustomLegend
              tooltipPreviousCopy={`${t(`dashboard.tooltipPreviousCopy.${planPreviousDateRange}`)}${
                planPreviousDateRange === 'lastYear' || planPreviousDateRange === 'previousQuarter'
                  ? ` (${getPlanPreviousDateRangeCopy(dateRange, planPreviousDateRange)})`
                  : ''
              }`}
            />
          </Box>

          <Grid container spacing={2}>
            {KPI_PLAN_TYPES.map((planType) => {
              const percentKpisByPlanType = getPercentKpisByPlanType({
                kpiPlanType: planType.id,
                data: data,
                planPreviousDateRange: planPreviousDateRange,
              });

              return (
                <Grid item xs={12} sm={6} md={MD_SIZES[planType.id]} key={planType.id}>
                  <KpisByPlanChart
                    planTitle={planType.label}
                    data={percentKpisByPlanType}
                    tooltipPreviousCopy={t(
                      `dashboard.tooltipPreviousCopy.${planPreviousDateRange}`,
                    )}
                  />
                </Grid>
              );
            })}
            {(() => {
              const actualRpKpis = getActualRpKpis({
                data: data,
                planPreviousDateRange: planPreviousDateRange,
              });

              return (
                <Grid item xs={12} sm={6} md={MD_SIZES.rp} key={'rp'}>
                  <KpisByPlanChart
                    data={actualRpKpis}
                    tooltipPreviousCopy={t(
                      `dashboard.tooltipPreviousCopy.${planPreviousDateRange}`,
                    )}
                  />
                </Grid>
              );
            })()}
          </Grid>
        </Box>
      </SalonDashboardWrapper>
    </Container>
  );
};
